import { useEffect, useState } from "react";
import { Hash } from "viem";
import { BoxProps, VStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { usePublicClient } from "wagmi";
import { INDEXER_REFETCH_DELAY_MS } from "@looksrare/config";
import {
  Text,
  BulkTransactionStep,
  BulkTransactionStepStatus,
  TransactionStepRow,
  StepErrorCta,
  StepStatus,
} from "@looksrare/uikit";
import { useHandleModalStep, useInvalidateEthBalance, useWalletClientIsReady, sleep } from "@looksrare/utils";
import { FlipperGameStore } from "../../hooks";

interface RefundStepProps extends BoxProps {
  status: StepStatus;
  collapse?: boolean;
  isCurrentStep: boolean;
  onComplete: () => void;
  onError: () => void;
  claimRefund: FlipperGameStore["claimRefund"];
}

export const RefundStep = ({
  status,
  collapse,
  isCurrentStep,
  onComplete,
  onError,
  claimRefund,
  ...props
}: RefundStepProps) => {
  const { isSignerReady } = useWalletClientIsReady();
  const publicClient = usePublicClient();
  const { t } = useTranslation();
  const [transaction, setTransaction] = useState<Hash>();
  const invalidateEthBalance = useInvalidateEthBalance();

  const { handleSubmit, isRejected, isIdle, isAccepted } = useHandleModalStep({
    onSubmit: async () => {
      const hash = await claimRefund();
      if (!hash) {
        throw new Error("No transaction hash found");
      }
      if (!publicClient) {
        throw new Error("No public client found");
      }
      setTransaction(hash);
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      if (receipt.status === "success") {
        invalidateEthBalance();
        // Wait for the transaction to be indexed
        await sleep(INDEXER_REFETCH_DELAY_MS);
        setTransaction(undefined);
      } else {
        throw new Error(`Refund failed. Transaction hash ${receipt.transactionHash}`);
      }
    },
    onSuccess: onComplete,
    onError,
  });

  const rowStatus = ((): BulkTransactionStepStatus => {
    if (isRejected) {
      return "error";
    }
    if (isIdle) {
      return "wait";
    }
    if (isAccepted) {
      return "done";
    }
    return "pending";
  })();

  useEffect(() => {
    if (isCurrentStep && isSignerReady) {
      handleSubmit({ callOnlyOnce: true });
    }
  }, [handleSubmit, isCurrentStep, isSignerReady]);

  const counterDisplay = "(1/1)";

  return (
    <BulkTransactionStep status={status} collapse={collapse} title={t("flipper::Claim Refund")} {...props}>
      <VStack spacing={4} alignItems="flex-start" width="100%">
        <Text textStyle="helper" bold color={isRejected ? "text-error" : "text-01"}>
          {isRejected
            ? t("flipper::You declined the transaction {{counterDisplay}}", { counterDisplay })
            : t("flipper::Confirm transaction in wallet {{counterDisplay}}", { counterDisplay })}
        </Text>
        <TransactionStepRow transactionHash={transaction} status={rowStatus} text={t("flipper::Claim Refund")} />
        {isRejected && <StepErrorCta onRetry={() => handleSubmit()} />}
      </VStack>
    </BulkTransactionStep>
  );
};
